import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "components/button";
import Icon from "components/icon";
import DateRangePicker from "components/date-range-picker";
import { Columns, TableSortState } from "components/table";
import ActionMenu from "components/shared/action-menu";
import { toast } from "react-toastify";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import useHistoryPanel from "hooks/shared/useHistoryPanel";
import { useAppTranslation } from "locale/useAppTranslation";
import { DistanceCondition } from "_services/modules/distanceType";
import { errorHandler } from "_services/errorHandler";
import { editDistanceTypePageRouteName } from "../edit/edit.page";
import {
  useDistanceTypeServiceV1ListDistanceTypesQuery,
  useDistanceTypeServiceV1ActivateDistanceTypesMutation,
  useDistanceTypeServiceV1DeactivateDistanceTypesMutation,
  type DistanceTypeV1DistanceTypeRes,
} from "_services/modules/distance-type";

const sortFieldMap: Record<string, string> = {
  distanceTypeName: "distance_type_name",
  distance: "distance_condition",
  status: "status",
  createdAt: "created_at",
  updatedAt: "updated_at",
};

export interface DistanceTypeRow {
  id: string;
  distanceTypeName: string;
  distanceCondition: DistanceCondition;
  distanceMin: number;
  distanceMax: number;
  description: string;
  status: "active" | "inactive";
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
}

const formatDistance = (row: DistanceTypeRow, kmUnit: string): string => {
  if (row.distanceCondition === "below") {
    return `< ${row.distanceMax.toLocaleString()} ${kmUnit}`;
  }
  if (row.distanceCondition === "between") {
    return `${row.distanceMin.toLocaleString()} ${kmUnit} - ${row.distanceMax.toLocaleString()} ${kmUnit}`;
  }
  if (row.distanceCondition === "above") {
    return `> ${row.distanceMin.toLocaleString()} ${kmUnit}`;
  }
  return "-";
};

const useDistanceTypeList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("distanceType");
  const [selectedKeys, setSelectedKeys] = useState<Array<string | number>>([]);
  const [sortState, setSortState] = useState<TableSortState>(null);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bulkAction, setBulkAction] = useState<string>("");
  const [showBulkConfirmModal, setShowBulkConfirmModal] = useState(false);
  const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
  const [toggleRow, setToggleRow] = useState<DistanceTypeRow | null>(null);
  const historyPanel = useHistoryPanel("distance_type");

  const [activateDistanceTypes] = useDistanceTypeServiceV1ActivateDistanceTypesMutation();
  const [deactivateDistanceTypes] = useDistanceTypeServiceV1DeactivateDistanceTypesMutation();

  const { data: apiResponse, isLoading, refetch } = useDistanceTypeServiceV1ListDistanceTypesQuery({
    page: currentPage,
    size: rowsPerPage,
    sort: sortState?.columnId ? (sortFieldMap[sortState.columnId] ?? sortState.columnId) : "created_at",
    order: sortState?.direction ?? "desc",
    status: filterValues.status || undefined,
    distanceTypeName: filterValues.distanceTypeName || undefined,
    distance: filterValues.distance || undefined,
    description: filterValues.description || undefined,
    createdAtFrom: filterValues.createdAt?.split("|")[0] || undefined,
    createdAtTo: filterValues.createdAt?.split("|")[1] || undefined,
    updatedAtFrom: filterValues.updatedAt?.split("|")[0] || undefined,
    updatedAtTo: filterValues.updatedAt?.split("|")[1] || undefined,
  });

  const tableData: DistanceTypeRow[] = useMemo(() => {
    if (!apiResponse?.distance_types) return [];
    return apiResponse.distance_types.map((item) => {
      const resp = item as DistanceTypeV1DistanceTypeRes & { distance_min?: number; distance_max?: number };
      return {
        id: resp.id || "",
        distanceTypeName: resp.distance_type_name ?? "",
        distanceCondition: (resp.distance_condition as DistanceCondition) ?? "between",
        distanceMin: resp.distance_min ?? 0,
        distanceMax: resp.distance_max ?? 0,
        description: resp.description ?? "",
        status: (resp.status as "active" | "inactive") ?? "inactive",
        createdBy: resp.created_by ?? "",
        createdAt: resp.created_at ?? "",
        updatedBy: resp.updated_by ?? "",
        updatedAt: resp.updated_at ?? "",
      };
    });
  }, [apiResponse]);

  const totalPages = apiResponse?.metadata?.total_page ?? 1;

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-list"),
        hasBackButton: false,
      })
    );
  }, [dispatch, t]);

  const selectedRows = tableData.filter((row) => selectedKeys.includes(row.id));
  const hasMixedStatus =
    selectedRows.length > 0 &&
    new Set(selectedRows.map((r) => r.status)).size > 1;

  const isBulkActionDisabled =
    selectedKeys.length === 0 || hasMixedStatus;

  const handleApplyBulkAction = () => {
    if (!bulkAction || isBulkActionDisabled) return;
    setShowBulkConfirmModal(true);
  };

  const handleConfirmBulkAction = async () => {
    try {
      const ids = selectedKeys.map(String);
      if (bulkAction === "activate") {
        await activateDistanceTypes({ distanceTypeV1DistanceTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-activate-success"));
      } else {
        await deactivateDistanceTypes({ distanceTypeV1DistanceTypeBulkStatusChangeReq: { ids } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      }
      setShowBulkConfirmModal(false);
      setBulkAction("");
      setSelectedKeys([]);
      refetch();
    } catch (error) {
      console.error("Bulk action failed:", error);
      errorHandler(error);
    }
  };

  const handleToggleStatus = (row: DistanceTypeRow) => {
    setToggleRow(row);
    setShowToggleConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    try {
      if (!toggleRow) return;
      if (toggleRow.status === "active") {
        await deactivateDistanceTypes({ distanceTypeV1DistanceTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-deactivate-success"));
      } else {
        await activateDistanceTypes({ distanceTypeV1DistanceTypeBulkStatusChangeReq: { ids: [toggleRow.id] } }).unwrap();
        toast.success(t("toast-activate-success"));
      }
      setShowToggleConfirmModal(false);
      setToggleRow(null);
      refetch();
    } catch (error) {
      console.error("Toggle status failed:", error);
      errorHandler(error);
    }
  };

  const handleFilterChange = (values: Record<string, string>) => {
    setFilterValues(values);
    setCurrentPage(1);
  };

  const handleEdit = (row: DistanceTypeRow) => {
    navigate(editDistanceTypePageRouteName(row.id.toString()));
  };

  const columns: Columns<DistanceTypeRow>[] = useMemo(
    () => [
      {
        id: "no",
        fieldId: "index",
        label: t("col-no"),
        width: "60px",
      },
      {
        id: "distanceTypeName",
        fieldId: "distanceTypeName",
        label: t("label-distance-type-name"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
      },
      {
        id: "distance",
        fieldId: "distanceCondition",
        label: t("label-distance"),
        sortable: true,
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm" data-testid={`col-distance-${row.id}`}>{formatDistance(row, t("col-unit-km"))}</span>
        ),
      },
      {
        id: "description",
        fieldId: "description",
        label: t("label-description"),
        filterable: true,
        filterPlaceholder: t("col-search-placeholder"),
        render: (row) => (
          <span className="text-sm text-greyScale-40 truncate max-w-[200px] inline-block" data-testid={`col-description-${row.id}`}>
            {row.description || "-"}
          </span>
        ),
      },
      {
        id: "status",
        fieldId: "status",
        label: t("label-status"),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t("col-all-data"), value: "" },
          { label: t("status-active"), value: "active" },
          { label: t("status-inactive"), value: "inactive" },
        ],
        render: (row) => (
          <div className="inline-flex items-center gap-2 text-sm" data-testid={`col-status-${row.id}`}>
            <span
              className={`h-3 w-3 rounded-full ${
                row.status === "active"
                  ? "bg-primary-600 border-2 border-primary-100"
                  : "bg-greyScale-60 border-2 border-greyScale-90"
              }`}
            />
            <span className="capitalize">{row.status}</span>
          </div>
        ),
      },
      {
        id: "createdAt",
        fieldId: "createdBy",
        fieldId2: "createdAt",
        label: t("col-created-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "updatedAt",
        fieldId: "updatedBy",
        fieldId2: "updatedAt",
        label: t("col-updated-by"),
        sortable: true,
        filterable: true,
        renderFilter: ({ value, onChange }) => {
          const [from = "", to = ""] = value.split("|");
          return (
            <DateRangePicker
              fromValue={from}
              toValue={to}
              onChange={(f, t) => onChange(`${f}|${t}`)}
              reserveHelperSpace={false}
            />
          );
        },
      },
      {
        id: "action",
        fieldId: "id",
        label: t("col-action"),
        width: "140px",
        dragDisabled: true,
        render: (row) => (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              type="button"
              variant="outline"
              color="primary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => handleEdit(row)}
              data-testid="btn-edit-distance"
            >
              {t("btn-edit")}
            </Button>
            <ActionMenu
              items={[
                {
                  label: row.status === "active" ? t("btn-deactivate") : t("btn-activate"),
                  onClick: () => handleToggleStatus(row),
                  variant: row.status === "active" ? "danger" : "default",
                  testId: row.status === "active" ? "btn-deactivate-distance" : "btn-activate-distance",
                },
                {
                  label: t("btn-track-history"),
                  onClick: () => historyPanel.openPanel(row.id, row.distanceTypeName),
                  testId: "btn-track-history-distance",
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [t],
  );

  return {
    data: tableData,
    isLoading,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggle,
    historyPanel,
    t,
  };
};

export default useDistanceTypeList;
